import { createPortal } from 'react-dom'
import { money } from '../utils/format'

// The printed / PDF version of an invoice or quote. It is hidden on screen
// and shown on its own when the page adds body.ci-printing and calls
// window.print(); it renders straight into <body> so the rest of the app
// can be dropped from the printout instead of leaving blank pages behind.
// Columns with nothing in them (discount, tax) are left out, and an
// invoice lists each payment and credit separately.


/** Text for a CSS string, e.g. the page footer. */
const cssString = (s) => `"${String(s || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, ' ')}"`

function initials(name) {
  return String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || 'AR'
}

/**
 * company: { name, tagline, logo, address: [], contact: [] }
 * title: 'Invoice' | 'Quote'; number; status: { label, tone } | null
 * billTo: { label, name, lines: [] }  (label defaults to Bill to)
 * facts: [[label, value]]  (dates, terms)
 * highlight: { label, value, note }  (balance due / quote total)
 * lines: [{ name, description, qty, unitPrice, discount, tax, amount }]
 * totals: [{ label, value, kind: 'minus' | 'grand' | 'due' }]
 * settlements: [{ date, label, detail, amount }]  (payments and credits)
 * notes, footer
 */
export default function DocumentPrint({ company, title, number, status, billTo, facts, highlight, lines, totals, settlements = [], notes, footer }) {
  const showDiscount = lines.some((l) => Number(l.discount) > 0.004)
  const showTax = lines.some((l) => Number(l.tax) > 0.004)
  const pageFooter = `${title} ${number}${company.name ? ` · ${company.name}` : ''}`

  return createPortal(
    <div className="ci-print-wrap" aria-hidden="true">
      {/* Margin boxes replace the browser's own date/URL header and footer. */}
      <style>{`@media print { @page { margin: 14mm 14mm 16mm; @bottom-left { content: ${cssString(pageFooter)}; font: 8.5px Inter, system-ui, sans-serif; color: #64748b; } @bottom-right { content: "Page " counter(page) " of " counter(pages); font: 8.5px Inter, system-ui, sans-serif; color: #64748b; } } }`}</style>
      <article className="docp">
        <header className="docp-head">
          <div className="docp-brand">
            {company.logo
              ? <img className="docp-logo" src={company.logo} alt="" />
              : <div className="docp-logo docp-logo-text">{initials(company.name)}</div>}
            <div>
              <div className="docp-company">{company.name}</div>
              {company.tagline && <div className="docp-muted">{company.tagline}</div>}
              {company.address.map((l) => <div key={l} className="docp-muted">{l}</div>)}
              {company.contact.length > 0 && <div className="docp-muted">{company.contact.join(' · ')}</div>}
            </div>
          </div>
          <div className="docp-title">
            <h1>{title}</h1>
            <div className="docp-number">{number}</div>
            {status && <span className={`docp-status tone-${status.tone}`}>{status.label}</span>}
          </div>
        </header>

        <section className="docp-parties">
          <div className="docp-billto">
            <div className="docp-label">{billTo.label || 'Bill to'}</div>
            <div className="docp-billto-name">{billTo.name}</div>
            {billTo.lines.map((l) => <div key={l} className="docp-muted">{l}</div>)}
          </div>
          <div className="docp-facts">
            <dl>
              {facts.map(([label, value]) => (
                <div key={label}><dt>{label}</dt><dd>{value || '—'}</dd></div>
              ))}
            </dl>
            {highlight && (
              <div className="docp-highlight">
                <span>{highlight.label}</span>
                <strong>{highlight.value}</strong>
                {highlight.note && <small>{highlight.note}</small>}
              </div>
            )}
          </div>
        </section>

        <table className="docp-items">
          <thead>
            <tr>
              <th className="docp-n">#</th>
              <th>Item</th>
              <th className="num">Qty</th>
              <th className="num">Unit price</th>
              {showDiscount && <th className="num">Discount</th>}
              {showTax && <th className="num">Tax</th>}
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td className="docp-n">{i + 1}</td>
                <td>
                  <div className="docp-item">{l.name || 'Item'}</div>
                  {l.description && <div className="docp-muted">{l.description}</div>}
                </td>
                <td className="num">{Number(l.qty || 0).toLocaleString('en-US', { maximumFractionDigits: 4 })}</td>
                <td className="num">{money(l.unitPrice)}</td>
                {showDiscount && <td className="num">{Number(l.discount) > 0.004 ? `−${money(l.discount)}` : '—'}</td>}
                {showTax && <td className="num">{Number(l.tax) > 0.004 ? money(l.tax) : '—'}</td>}
                <td className="num docp-strong">{money(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="docp-lower">
          <div className="docp-notes">
            {notes && (
              <>
                <div className="docp-label">Notes</div>
                <p>{notes}</p>
              </>
            )}
          </div>
          <table className="docp-totals">
            <tbody>
              {totals.map((t) => (
                <tr key={t.label} className={t.kind ? `is-${t.kind}` : undefined}>
                  <th>{t.label}</th>
                  <td>{t.kind === 'minus' ? `−${money(t.value)}` : money(t.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {settlements.length > 0 && (
          <section className="docp-settlements">
            <div className="docp-label">Payments and credits received</div>
            <table>
              <thead>
                <tr><th>Date</th><th>Received as</th><th>Reference</th><th className="num">Amount</th></tr>
              </thead>
              <tbody>
                {settlements.map((s, i) => (
                  <tr key={i}>
                    <td>{s.date}</td>
                    <td>{s.label}</td>
                    <td className="docp-muted">{s.detail || '—'}</td>
                    <td className="num">{money(s.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {footer && <footer className="docp-footer">{footer}</footer>}
      </article>
    </div>,
    document.body,
  )
}
